import { useMutation } from '@apollo/client/react';
import { filter } from 'lodash';
import { useState } from 'react';

import useDirectUpload from './useDirectUpload';

import { type Attachment, DestroyAttachmentDocument } from '@/graphql/generated/graphql';
import { MAX_FILE_SIZE_IN_MEGA_BYTES, MAX_ATTACHMENTS_ALLOWED } from '@/utils/constants';
import { arrayLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

export const useFileUploader = () => {
  const [files, setFiles] = useState<Attachment[]>([]);
  const { isUploading, error: uploadError, uploadFile } = useDirectUpload();
  const [destroyAttachment] = useMutation(DestroyAttachmentDocument);

  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>, key?: string) => {
    if (e.target.files && arrayLength(e.target.files) > 0) {
      const file = e.target.files[0];

      if (file.size / 1000000 > MAX_FILE_SIZE_IN_MEGA_BYTES) {
        displayNotification({
          type: 'error',
          text: `Max allowed file size is ${MAX_FILE_SIZE_IN_MEGA_BYTES} MB.`,
        });
        return;
      }

      uploadFile(file, key).then((response) => {
        if (!response?.id) return;

        setFiles((prev) => [
          ...prev,
          {
            id: response.id,
            signedBlobId: response.signedBlobId,
            filename: response.filename,
            byteSize: response.byteSize,
            previewUrl: response.previewUrl,
            tags: [],
          },
        ]);

        if (uploadError) {
          displayNotification({ type: 'error', text: uploadError });
        }

        return response.id;
      });
    }
  };

  const handleRemoveImage = (id: string) => {
    destroyAttachment({
      variables: { id },
      onCompleted: () => {
        setFiles((prev) => filter(prev, (file) => file.id !== id));
      },
      onError: (err) => {
        displayNotification({ type: 'error', text: err.message });
      },
    });
  };

  return {
    isUploading,
    shouldDisableUploadButton: arrayLength(files) >= MAX_ATTACHMENTS_ALLOWED || isUploading,
    files,
    handleUploadImage,
    handleRemoveImage,
  };
};

export default useFileUploader;
